import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import { FiSend, FiDownload, FiCalendar, FiX, FiPlus, FiExternalLink } from 'react-icons/fi';
import { MdSearch } from 'react-icons/md';
import { Header, CustomPagination, TransferModal } from '../../components';
import { useStateContext } from '../../contexts/ContextProvider';
import useKYCStatusRedirect from '../../hooks/useKYCStatusRedirect';
import { userRequest } from '../../utils/requestMethods';
import { formatCurrency } from '../../utils/currencyUtils';
import { exportToCSV } from '../../utils/exportCSV';
import SkeletonTable from '../../components/common/SkeletonTable';
import FundSelector from '../../components/Finance/FundSelector';
import RecordExpenseModal from '../../components/Finance/RecordExpenseModal';

const customStyles = {
  headRow: {
    style: { backgroundColor: 'rgb(248, 250, 252)', fontWeight: 'bold' },
  },
};

const getExpenseSource = (expense) => {
  if (expense.source) return expense.source;
  if (expense.salary_batch_id) return 'payroll';
  if (expense.banking_reference && !expense.banking_reference.startsWith('expense_')) {
    return 'online_transfer';
  }
  return 'offline_record';
};

const expenseSourceLabels = {
  online_transfer: 'Online Transfer',
  offline_record: 'Offline Record',
  payroll: 'Payroll',
};

const FinanceExpenses = () => {
  useKYCStatusRedirect('/finance/expenses');
  const { school } = useStateContext();
  const schoolId = school?._id;
  const multiFundEnabled = !!school?.settings?.finance?.multi_fund_enabled;

  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [balance, setBalance] = useState({});
  const [fundId, setFundId] = useState('');
  const [category, setCategory] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isRecordOpen, setIsRecordOpen] = useState(false);
  const [isTransferOpen, setIsTransferOpen] = useState(false);
  const [filterText, setFilterText] = useState('');
  const filterRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) {
        setShowDateFilter(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!schoolId) return;
    const fetchBalance = async () => {
      try {
        const { data } = await userRequest.get(`banking/wallets/${schoolId}/balance`);
        setBalance(data.data ?? data);
      } catch (err) {
        console.error(err);
      }
    };
    fetchBalance();
  }, [schoolId]);

  useEffect(() => {
    if (!schoolId) return;
    const fetchCategories = async () => {
      try {
        const { data } = await userRequest.get(`/schools/${schoolId}/finance/expense-categories`);
        setCategories([...(data.system || []), ...(data.custom || [])]);
      } catch (error) {
        console.log(error);
      }
    };
    fetchCategories();
  }, [schoolId]);

  const fetchExpenses = async () => {
    if (!schoolId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '100', page: '1' });
      if (fundId) params.set('fund_id', fundId);
      if (category) params.set('category', category);
      if (typeFilter && typeFilter !== 'all') params.set('type', typeFilter);
      if (startDate) params.set('startDate', new Date(startDate).toISOString().slice(0, 10));
      if (endDate) params.set('endDate', new Date(endDate).toISOString().slice(0, 10));
      const { data } = await userRequest.get(`/schools/${schoolId}/expenses?${params.toString()}`);
      setExpenses(data.expenses || data.items || []);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
      setShowDateFilter(false);
    }
  };

  useEffect(() => {
    fetchExpenses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId, fundId, category, typeFilter]);

  const clearDateFilter = () => {
    setStartDate('');
    setEndDate('');
    setShowDateFilter(false);
  };

  const columns = [
    {
      name: 'Title',
      cell: row => <span className="font-medium text-gray-800">{row.title}</span>,
      exportCell: row => row.title,
    },
    {
      name: 'Category',
      cell: row => <span className="text-sm text-gray-600">{row.category_name}</span>,
      exportCell: row => row.category_name,
    },
    {
      name: 'Fund',
      cell: row => <span className="text-sm text-gray-600">{row.fund?.name || '—'}</span>,
      exportCell: row => row.fund?.name || '—',
    },
    {
      name: 'Type',
      cell: row => {
        const source = getExpenseSource(row);
        if (source === 'online_transfer') return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200">
            <FiExternalLink className="w-3 h-3" /> Online Transfer
          </span>
        );
        if (source === 'payroll') return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
            Payroll
          </span>
        );
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
            Offline Record
          </span>
        );
      },
      exportCell: row => expenseSourceLabels[getExpenseSource(row)] || 'Expense',
    },
    {
      name: 'Amount',
      cell: row => (
        <span className={`font-semibold ${getExpenseSource(row) === 'offline_record' ? 'text-gray-700' : 'text-red-600'}`}>
          {formatCurrency(row.amount)}
        </span>
      ),
      exportCell: row => formatCurrency(row.amount),
    },
    {
      name: 'Beneficiary',
      cell: row => <span className="text-sm text-gray-600">{row.beneficiary}</span>,
      exportCell: row => row.beneficiary,
    },
    {
      name: 'Date',
      cell: row => new Date(row.expense_date).toLocaleDateString('en-NG', { dateStyle: 'medium' }),
      sortable: true,
    },
  ];

  const filteredData = expenses.filter(item =>
    item.title?.toLowerCase().includes(filterText.toLowerCase()) ||
    item.beneficiary?.toLowerCase().includes(filterText.toLowerCase())
  );

  const hasDateFilter = startDate && endDate;

  return (
    <div className="m-2 md:m-10 mt-24 p-4 md:p-10 dark:bg-secondary-dark-bg bg-white rounded-3xl min-h-screen">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
        <Header
          category="Finance"
          title="Expenses & Disbursements"
          description="Track platform-processed disbursements and keep separate informational records of expenses paid outside Acceede."
        />
        <div className="flex items-center gap-2.5 shrink-0 flex-wrap">
          <div className="relative" ref={filterRef}>
            <button
              type="button"
              onClick={() => setShowDateFilter(!showDateFilter)}
              className={`inline-flex items-center gap-2 py-2 px-4 text-sm font-medium rounded-full transition-all
                ${hasDateFilter
                  ? 'bg-indigo-50 text-indigo-700 border border-indigo-200'
                  : 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100'
                }`}
            >
              <FiCalendar className="w-4 h-4" />
              {hasDateFilter ? (
                <span>
                  {new Date(startDate).toLocaleDateString('en-NG', { month: 'short', day: 'numeric' })}
                  {' — '}
                  {new Date(endDate).toLocaleDateString('en-NG', { month: 'short', day: 'numeric' })}
                </span>
              ) : 'Date Range'}
              {hasDateFilter && (
                <span
                  role="button"
                  onClick={e => { e.stopPropagation(); clearDateFilter(); fetchExpenses(); }}
                  className="ml-1 p-0.5 rounded-full hover:bg-indigo-100"
                >
                  <FiX className="w-3.5 h-3.5" />
                </span>
              )}
            </button>

            {showDateFilter && (
              <div className="absolute right-0 top-full mt-2 z-50 bg-white border border-gray-200 shadow-lg rounded-2xl p-5 w-72">
                <p className="text-sm font-semibold text-gray-700 mb-4">Filter by date range</p>
                <div className="space-y-3">
                  <div>
                    <label className="block mb-1.5 text-xs font-medium text-gray-500">Start Date</label>
                    <input
                      type="date" value={startDate}
                      onChange={e => setStartDate(e.target.value)}
                      className="bg-gray-50 border border-gray-200 text-sm rounded-xl focus:ring-indigo-500 focus:border-indigo-500 block w-full p-2.5"
                    />
                  </div>
                  <div>
                    <label className="block mb-1.5 text-xs font-medium text-gray-500">End Date</label>
                    <input
                      type="date" value={endDate}
                      onChange={e => setEndDate(e.target.value)}
                      className="bg-gray-50 border border-gray-200 text-sm rounded-xl focus:ring-indigo-500 focus:border-indigo-500 block w-full p-2.5"
                    />
                  </div>
                  <div className="flex gap-2 pt-1">
                    <button
                      type="button" onClick={clearDateFilter}
                      className="flex-1 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors"
                    >
                      Clear
                    </button>
                    <button
                      type="button" onClick={fetchExpenses}
                      disabled={loading || !startDate || !endDate}
                      className="flex-1 py-2 text-sm font-medium text-white bg-indigo-600 rounded-full hover:bg-indigo-700 disabled:bg-indigo-300 transition-colors"
                    >
                      {loading ? 'Searching…' : 'Apply'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={() => setIsTransferOpen(true)}
            className="inline-flex items-center gap-2 py-2 px-4 text-sm font-medium rounded-full text-indigo-700 bg-indigo-50 border border-indigo-200 hover:bg-indigo-100 transition-colors shadow-sm"
          >
            <FiSend className="w-4 h-4" />
            Send Money (Bank Transfer)
          </button>
          <button
            type="button"
            onClick={() => setIsRecordOpen(true)}
            className="inline-flex items-center gap-2 py-2 px-4 text-sm font-medium rounded-full text-white bg-indigo-600 hover:bg-indigo-700 shadow-sm transition-colors"
          >
            <FiPlus className="w-4 h-4" />
            Record Offline Expense
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="w-48">
            <FundSelector schoolId={schoolId} value={fundId} onChange={setFundId} includeAllOption />
          </div>
          <select
            value={category}
            onChange={e => setCategory(e.target.value)}
            className="rounded-xl bg-gray-50 border border-gray-200 text-sm px-4 py-2.5 focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="">All Categories</option>
            {categories.map(cat => (
              <option key={cat.slug} value={cat.slug}>{cat.name}</option>
            ))}
          </select>
          <Link to="/finance/expense-categories" className="text-sm font-medium text-indigo-600 hover:text-indigo-700">
            Manage Categories
          </Link>
        </div>

        <div className="flex items-center gap-1.5 p-1 bg-gray-100/80 rounded-2xl">
          {[
            { id: 'all', label: 'All Records' },
            { id: 'online_transfer', label: 'Online Transfers' },
            { id: 'offline_record', label: 'Offline Records' },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTypeFilter(t.id)}
              className={`px-3.5 py-1.5 text-xs font-semibold rounded-xl transition-all ${
                typeFilter === t.id
                  ? 'bg-white text-indigo-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <MdSearch className="text-gray-400" />
          </div>
          <input
            type="text"
            value={filterText}
            onChange={e => setFilterText(e.target.value)}
            placeholder="Search expenses & beneficiaries..."
            className="pl-9 pr-4 py-2 text-sm rounded-full bg-gray-50 border border-gray-200 focus:ring-indigo-500 focus:border-indigo-500 w-48 md:w-64"
          />
        </div>
        <button
          onClick={() => exportToCSV(filteredData, columns, 'expenses.csv')}
          className="inline-flex items-center gap-2 py-2 px-4 text-sm font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-full transition-colors"
        >
          <FiDownload className="w-4 h-4" />
          Export current page
        </button>
      </div>

      <div className="border border-gray-200 rounded-2xl overflow-hidden">
        <DataTable
          data={filteredData}
          columns={columns}
          getRowId={row => row._id}
          pagination
          paginationComponent={CustomPagination}
          paginationRowsPerPageOptions={[10, 20, 30, 50]}
          customStyles={customStyles}
          progressPending={loading}
          progressComponent={<SkeletonTable rows={5} columns={7} />}
          highlightOnHover
          persistTableHead
          noDataComponent={
            <div className="py-14 text-center text-sm text-gray-400">No expenses or disbursements found.</div>
          }
        />
      </div>

      {isTransferOpen && (
        <TransferModal
          closeModal={() => setIsTransferOpen(false)}
          school={school}
          balance={balance}
          onSuccess={fetchExpenses}
        />
      )}

      {isRecordOpen && (
        <RecordExpenseModal
          isOpen={isRecordOpen}
          onClose={() => setIsRecordOpen(false)}
          schoolId={schoolId}
          onCreated={fetchExpenses}
        />
      )}
    </div>
  );
};

export default FinanceExpenses;
